import { Box, Divider, Skeleton, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import {
    SectionTitleSkeleton,
    SectionItemLabelSkeleton,
    SectionItemDataSkeleton,
    TableColumnLabelSkeleton,
} from "../../../../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    orderContainer: {
        padding: "3.875rem",
        [theme.breakpoints.down("sm")]: {
            padding: "2rem 1.5rem",
        },
        height: "fit-content",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        [theme.breakpoints.down("sm")]: {
            boxShadow: "none",
            width: "100%",
        },
        "@media print": {
            marginBottom: 0,
            paddingTop: "1.5rem",
            paddingBottom: 0,
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
            width: "100%",
            boxShadow: "none",
            backgroundColor: "white",
            pageBreakInside: "avoid",
            breakInside: "avoid",
            pageBreakAfter: "always",
            breakAfter: "page",
            "&:last-child": {
                pageBreakAfter: "auto",
                breakAfter: "auto",
            },
        },
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    qrWrap: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    mainContent: {
        display: "flex",
        flexWrap: "nowrap",
        gap: "100px",
        marginTop: theme.spacing(5),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: theme.spacing(2.5),
            marginTop: "3.25rem",
        },
        "@media print": {
            display: "flex",
            flexDirection: "row",
            gap: "100px",
        },
    },
    leftColumn: {
        flex: "0 0 35%",
        maxWidth: "35%",
        [theme.breakpoints.down("md")]: {
            flex: "1 1 100%",
            maxWidth: "100%",
        },
        "@media print": {
            flex: "0 0 35%",
            maxWidth: "35%",
        },
    },
    rightColumn: {
        flex: "0 0 55%",
        maxWidth: "55%",
        [theme.breakpoints.down("md")]: {
            flex: "1 1 100%",
            maxWidth: "100%",
        },
        "@media print": {
            flex: "0 0 55%",
            maxWidth: "55%",
        },
    },
    orderInfo: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(3),
        },
    },
    sectionContainer: {
        margin: theme.spacing(4, 0),
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(3, 0),
        },
    },
    detailsContainer: {
        gap: "0.25rem",
    },
    tableHeader: {
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: "1rem",
        padding: "0.5rem 0",
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableRow: {
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: "1rem",
        padding: "1rem 0",
        alignItems: "center",
    },
    tableDivider: {
        [theme.breakpoints.up("md")]: {
            display: "none",
        },
    },
}))

function SkeletonDetailRow() {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <SectionItemLabelSkeleton width={100} />
            <SectionItemDataSkeleton width={120} />
        </Stack>
    )
}

function SkeletonContactRow() {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
            <SectionItemLabelSkeleton width={80} />
            <Stack alignItems="flex-end" gap={0.5}>
                <SectionItemDataSkeleton width={140} />
                <SectionItemLabelSkeleton width={160} />
            </Stack>
        </Stack>
    )
}

function SkeletonTableRow() {
    const { classes } = useStyles()

    return (
        <Box className={classes.tableRow}>
            <SectionItemDataSkeleton width={30} />
            <SectionItemDataSkeleton width={180} />
            <SectionItemDataSkeleton width={60} />
        </Box>
    )
}

export function PackageSlipSkeleton() {
    const { classes } = useStyles()

    return (
        <Box className={classes.orderContainer}>
            {/* Header */}
            <Box className={classes.header}>
                <Skeleton variant="text" width={180} height={48} animation="wave" />
                <Box className={classes.qrWrap}>
                    <SectionItemLabelSkeleton width={80} />
                    <Skeleton variant="rectangular" width={80} height={80} animation="wave" />
                </Box>
            </Box>

            {/* Main Content */}
            <Box className={classes.mainContent}>
                {/* Left Column */}
                <Box className={classes.leftColumn}>
                    {/* Order Info */}
                    <Box className={classes.orderInfo}>
                        <Skeleton variant="text" width={160} height={32} animation="wave" />
                        <SectionItemLabelSkeleton width={180} />
                    </Box>

                    <Divider />

                    {/* Delivery Details */}
                    <Box className={classes.sectionContainer}>
                        <SectionTitleSkeleton width={140} />
                        <Stack className={classes.detailsContainer} sx={{ mt: 2 }}>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <SkeletonDetailRow key={index} />
                            ))}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Contact Details */}
                    <Box className={classes.sectionContainer}>
                        <SectionTitleSkeleton width={130} />
                        <Stack sx={{ mt: 2.5 }}>
                            <SkeletonContactRow />
                            <SkeletonContactRow />
                        </Stack>
                    </Box>
                </Box>

                {/* Right Column */}
                <Box className={classes.rightColumn}>
                    <Divider className={classes.tableDivider} />

                    {/* Table Header */}
                    <Box className={classes.tableHeader}>
                        <TableColumnLabelSkeleton width={40} />
                        <TableColumnLabelSkeleton width={70} />
                        <TableColumnLabelSkeleton width={90} />
                    </Box>

                    {/* Table Rows */}
                    {Array.from({ length: 4 }).map((_, index) => (
                        <SkeletonTableRow key={index} />
                    ))}
                </Box>
            </Box>
        </Box>
    )
}
